import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { Close } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Link,
  Switch,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  autoClosedColorResolver,
  draftColorResolver,
  pendingMitigationColorResolver,
  publishedColorResolver,
} from 'src/components/atlas/organisms/helpers/sharedStatusColorResolvers';
import { getSlugFromFormType } from 'src/components/pages/FormCreation/helpers/formTypeMapper';
import { FormType } from 'src/components/pages/CoiFormManagement/types';
import { GetCategoryConfigCategoriesQuery } from 'src/graphql/generated';
import { DateFormats, formatDate } from 'src/utils/time';
import { ReadOnlyRichText } from 'src/components/atoms/ReadOnlyRichText/ReadOnlyRichText';
import { CategoryStatusBadge } from '../atoms/CategoryStatusBadge';
import { useCategoryLinkedForms } from '../hooks/useCategoryLinkedForms';

type Category = NonNullable<
  GetCategoryConfigCategoriesQuery['getOrganisationCategories']
>[number];

interface CategoryDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  category: Category | null;
  isCOI?: boolean;
}

const DEFAULT_LOCALE = 'en-GB';

const resolveCategoryName = (category: Category | null): string | undefined => {
  const locales = category?.locales as
    | Record<string, { name?: string } | undefined>
    | undefined;
  if (!locales) return undefined;
  return (
    locales[DEFAULT_LOCALE]?.name ??
    Object.values(locales).find((locale) => locale?.name)?.name
  );
};

const resolveCategoryDescription = (category: Category | null): string => {
  const locales = category?.locales as
    | Record<string, { description?: string } | undefined>
    | undefined;
  return locales?.[DEFAULT_LOCALE]?.description ?? '';
};

type LensTokens = LensTheme['tokens'];
type LensColor = LensTokens['semantic']['color'];

const LINKED_FORM_STATUS_COLORS: Record<
  string,
  (color: LensColor, tokens: LensTokens) => string
> = {
  Published: (_color, tokens) => publishedColorResolver(tokens).backgroundColor,
  Draft: (_color, tokens) => draftColorResolver(tokens).backgroundColor,
  Unpublished: (_color, tokens) =>
    pendingMitigationColorResolver(tokens).backgroundColor,
  Archived: (_color, tokens) => autoClosedColorResolver(tokens).backgroundColor,
};

const getLinkedFormStatusColor = (
  status: string,
  color: LensColor,
  tokens: LensTokens,
): string =>
  (LINKED_FORM_STATUS_COLORS[status] ?? (() => color.surface.variant.value))(
    color,
    tokens,
  );

const ReadOnlyToggle: React.FC<{ label: string; checked: boolean }> = ({
  label,
  checked,
}) => {
  const { tokens } = useTheme<LensTheme>();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <Switch
        checked={checked}
        disabled
        data-testid={`category-details-toggle-${label}`}
        sx={{ flexShrink: 0 }}
        slotProps={{ input: { 'aria-label': label } }}
      />
      <Typography
        sx={{
          fontSize: '14px',
          color: tokens.semantic.color.type.default.value,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const DetailRow: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => {
  const { tokens } = useTheme<LensTheme>();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '24px',
      }}
    >
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: tokens.semantic.fontWeight.emphasis.value,
          color: tokens.semantic.color.type.default.value,
          flexShrink: 0,
          minWidth: '160px',
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          flex: 1,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export const CategoryDetailsDrawer: React.FC<CategoryDetailsDrawerProps> = ({
  open,
  onClose,
  category,
  isCOI = false,
}) => {
  const { tokens } = useTheme<LensTheme>();
  const history = useHistory();
  const sp = tokens.semantic.spacing.desktop.gap;
  const color = tokens.semantic.color;

  const categoryName = resolveCategoryName(category);
  const categoryDescription = resolveCategoryDescription(category);

  const handleSeeForm = (formId: string) => {
    const formTypeSlug = getSlugFromFormType(
      isCOI ? FormType.CONFLICT_OF_INTEREST : FormType.SPEAK_UP,
    );
    history.push(`/form-management/${formTypeSlug}/form/${formId}`, {
      returnTo: '/categories',
    });
  };

  const isCustomFlow =
    isCOI || (!!category?.isReportedCategory && !!category?.isCustomQuestions);

  const {
    forms,
    isLoading: isLoadingForms,
    isError: isFormsError,
  } = useCategoryLinkedForms(
    category?.id,
    open && isCustomFlow,
    isCOI ? FormType.CONFLICT_OF_INTEREST : FormType.SPEAK_UP,
  );

  const showReportedSections =
    !isCOI && (category?.isReportedCategory ?? false);

  const sectionTitleSx = {
    fontSize: '14px',
    fontWeight: tokens.semantic.fontWeight.emphasis.value,
    color: color.type.default.value,
  };
  const labelSx = { fontSize: '12px', color: color.type.muted.value };
  const valueSx = { fontSize: '14px', color: color.type.default.value };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: false }}
      data-testid="category-details-drawer"
      sx={{
        '& .MuiDrawer-paper': {
          width: '567px',
          padding: sp[3].value,
          backgroundColor: color.surface.default.value,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '100vh',
          overflow: 'auto',
        },
      }}
    >
      {category && (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: sp[3].value,
              flexShrink: 0,
              gap: '12px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                minWidth: 0,
              }}
            >
              <Tooltip
                title={categoryName ?? 'Untitled'}
                placement="bottom-start"
              >
                <Typography
                  component="h2"
                  tabIndex={0}
                  sx={{
                    fontSize: '24px',
                    lineHeight: 1.4,
                    fontWeight: tokens.semantic.fontWeight.emphasis.value,
                    color: color.type.default.value,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {categoryName ?? 'Untitled'}
                </Typography>
              </Tooltip>
              <CategoryStatusBadge status={category.status} />
            </Box>
            <IconButton
              onClick={onClose}
              aria-label="close"
              data-testid="category-details-close"
              sx={{
                flexShrink: 0,
                color: color.action.secondary.onSecondary.value,
                '&:hover': {
                  backgroundColor: color.action.secondary.hoverFill.value,
                },
              }}
            >
              <Close />
            </IconButton>
          </Box>

          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              padding: '24px',
              borderRadius: '12px',
              backgroundColor: color.surface.variant.value,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                padding: '24px',
                borderRadius: '12px',
                backgroundColor: color.surface.default.value,
              }}
            >
              {isCOI ? (
                <DetailRow label="Intake channels">
                  <Typography sx={valueSx}>Vault</Typography>
                </DetailRow>
              ) : (
                <>
                  <DetailRow label="Type">
                    <ReadOnlyToggle
                      label="Determined category"
                      checked={category.isDeterminedCategory}
                    />
                    <ReadOnlyToggle
                      label="Reported category"
                      checked={category.isReportedCategory}
                    />
                  </DetailRow>

                  {showReportedSections && (
                    <>
                      <Divider />
                      <DetailRow label="Report type">
                        <Typography sx={valueSx}>
                          {category.question ? 'Inquiry' : 'Misconduct report'}
                        </Typography>
                      </DetailRow>
                    </>
                  )}

                  {showReportedSections && (
                    <>
                      <Divider />
                      <DetailRow label="Intake channels">
                        {isCustomFlow ? (
                          <ReadOnlyToggle
                            label="Vault"
                            checked={category.displayInOpenReporting}
                          />
                        ) : (
                          <>
                            <ReadOnlyToggle
                              label="VaultTalk"
                              checked={category.displayInVaultTalk}
                            />
                            <ReadOnlyToggle
                              label="Slack"
                              checked={
                                category.displayInCollaborationToolIntegration
                              }
                            />
                            <ReadOnlyToggle
                              label="Vault"
                              checked={category.displayInOpenReporting}
                            />
                          </>
                        )}
                      </DetailRow>
                    </>
                  )}

                  {showReportedSections && (
                    <>
                      <Divider />
                      <DetailRow label="Submission type">
                        <ReadOnlyToggle
                          label="GoTogether"
                          checked={category.allowGoTogether}
                        />
                        <ReadOnlyToggle
                          label="GoAhead"
                          checked={!category.allowGoTogether}
                        />
                      </DetailRow>
                    </>
                  )}

                  {showReportedSections &&
                    !category.isCustomQuestions &&
                    categoryDescription && (
                      <>
                        <Divider />
                        <DetailRow label="Description">
                          <ReadOnlyRichText
                            value={categoryDescription}
                            data-testid="category-details-description"
                          />
                        </DetailRow>
                      </>
                    )}
                </>
              )}

              <Divider />
              <DetailRow label="Created by">
                <Typography sx={valueSx}>
                  {category.Author?.displayName ?? category.Author?.name ?? '—'}
                </Typography>
              </DetailRow>

              <Divider />
              <DetailRow label="Created at">
                <Typography sx={valueSx}>
                  {formatDate(
                    category.createdAt,
                    DateFormats.YYYY_MM_DD_TIME_SHORT,
                    false,
                  )}
                </Typography>
              </DetailRow>

              <Divider />
              <DetailRow label="Last updated">
                <Typography sx={valueSx}>
                  {formatDate(
                    category.updatedAt,
                    DateFormats.YYYY_MM_DD_TIME_SHORT,
                    false,
                  )}
                </Typography>
              </DetailRow>
            </Box>
          </Box>

          {isCustomFlow && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                mt: '20px',
              }}
            >
              <Typography sx={sectionTitleSx}>Linked forms</Typography>
              {isLoadingForms ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '16px',
                  }}
                  data-testid="category-details-forms-loading"
                >
                  <CircularProgress size={24} />
                </Box>
              ) : isFormsError ? (
                <Typography
                  sx={labelSx}
                  data-testid="category-details-forms-error"
                >
                  Unable to load linked forms
                </Typography>
              ) : forms.length === 0 ? (
                <Typography
                  sx={labelSx}
                  data-testid="category-details-no-forms"
                >
                  No linked forms
                </Typography>
              ) : (
                forms.map((form) => (
                  <Box
                    key={form.id}
                    data-testid={`category-details-linked-form-${form.id}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px',
                      padding: '12px 16px',
                      backgroundColor: color.surface.default.value,
                      border: `1px solid ${color.ui.divider.default.value}`,
                      borderRadius: '8px',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <Typography sx={valueSx}>{form.title}</Typography>
                      <Box
                        component="span"
                        data-testid={`category-details-form-status-${form.id}`}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          height: '24px',
                          padding: '0 12px',
                          borderRadius: '9999px',
                          fontSize: '12px',
                          fontWeight: tokens.semantic.fontWeight.emphasis.value,
                          color: color.type.default.value,
                          backgroundColor: getLinkedFormStatusColor(
                            form.status,
                            color,
                            tokens,
                          ),
                        }}
                      >
                        {form.status}
                      </Box>
                    </Box>
                    <Link
                      component="button"
                      underline="none"
                      onClick={() => handleSeeForm(form.id)}
                      sx={{
                        fontSize: '14px',
                        fontWeight: tokens.semantic.fontWeight.emphasis.value,
                        color: color.type.default.value,
                        padding: '6px 16px',
                        borderRadius: '9999px',
                        border: `1px solid ${color.ui.divider.default.value}`,
                        flexShrink: 0,
                      }}
                      data-testid={`category-details-see-form-${form.id}`}
                    >
                      See form
                    </Link>
                  </Box>
                ))
              )}
            </Box>
          )}
        </>
      )}
    </Drawer>
  );
};
